import type { SavedQuery } from '../types/query';
import { PUBLIC_API_URL } from '../config/api';

export interface SwaggerSpec {
  openapi: string;
  info: {
    title: string;
    description: string;
    version: string;
  };
  servers: { url: string; description: string }[];
  paths: Record<string, unknown>;
  components: {
    schemas: Record<string, unknown>;
    parameters: Record<string, unknown>;
    securitySchemes: Record<string, unknown>;
  };
  security: Record<string, unknown[]>[];
}

class SwaggerService {
  generateSpec(query: SavedQuery): SwaggerSpec {
    const columnsToShow = query.selected_columns.length > 0
      ? query.selected_columns
      : [];

    const properties: Record<string, { type: string; description?: string }> = {};
    columnsToShow.forEach(col => {
      properties[col] = { type: 'string' };
    });

    const parameters = query.filter_columns.map(col => ({
      name: col,
      in: 'query' as const,
      required: false,
      schema: { type: 'string' },
      description: `Filtrar por ${col}`,
    }));

    const pathKey = `/v2/${query.endpoint_slug}`;

    const spec: SwaggerSpec = {
      openapi: '3.0.3',
      info: {
        title: query.name,
        description: query.documentation_description || query.description || `API do servico ${query.name}`,
        version: '1.0.0',
      },
      servers: [
        {
          url: PUBLIC_API_URL,
          description: 'Servidor principal',
        },
      ],
      paths: {
        [pathKey]: {
          get: {
            summary: query.name,
            description: query.documentation_description || query.description || '',
            operationId: `get_${query.endpoint_slug.replace(/-/g, '_')}`,
            parameters,
            security: [
              { BearerAuth: [] },
              { PersonalKey: [] },
            ],
            responses: {
              '200': {
                description: 'Resposta com sucesso',
                content: {
                  'application/json': {
                    schema: {
                      type: 'object',
                      properties: {
                        success: { type: 'boolean', example: true },
                        data: {
                          type: 'array',
                          items:
                            Object.keys(properties).length > 0
                              ? { type: 'object', properties }
                              : { type: 'object' },
                        },
                        metadata: {
                          type: 'object',
                          properties: {
                            total: { type: 'integer', example: 100 },
                            timestamp: { type: 'string', format: 'date-time' },
                          },
                        },
                      },
                    },
                  },
                },
              },
              '400': {
                description: 'Requisicao invalida',
                content: {
                  'application/json': {
                    schema: {
                      type: 'object',
                      properties: {
                        success: { type: 'boolean', example: false },
                        error: { type: 'string' },
                      },
                    },
                  },
                },
              },
              '500': {
                description: 'Erro interno do servidor',
                content: {
                  'application/json': {
                    schema: {
                      type: 'object',
                      properties: {
                        success: { type: 'boolean', example: false },
                        error: { type: 'string' },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
      components: {
        schemas: {},
        parameters: {},
        securitySchemes: {
          BearerAuth: {
            type: 'http',
            scheme: 'bearer',
            bearerFormat: 'token',
            description: 'Token de acesso do usuario (access_token)',
          },
          PersonalKey: {
            type: 'apiKey',
            in: 'header',
            name: 'personal-key',
            description: 'Chave pessoal do usuario (personal_key)',
          },
        },
      },
      security: [
        { BearerAuth: [] },
        { PersonalKey: [] },
      ],
    };

    return spec;
  }

  generateSpecJson(query: SavedQuery): string {
    return JSON.stringify(this.generateSpec(query), null, 2);
  }
}

export const swaggerService = new SwaggerService();
